import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

const APP_ENV = (process.env.APP_ENV ?? 'test') as 'test' | 'production';
const isProduction = APP_ENV === 'production';

function pick<T>(test: T, prod: T): T {
  return isProduction ? prod : test;
}

// ── Aufgelöste Env-Variablen (APP_ENV-abhängig) ──

export const SUPABASE_URL = pick(
  process.env.SUPABASE_TEST_URL!,
  process.env.SUPABASE_PROD_URL!,
);

export const SUPABASE_ANON_KEY = pick(
  process.env.SUPABASE_TEST_ANON_KEY!,
  process.env.SUPABASE_PROD_ANON_KEY!,
);

export const SUPABASE_SERVICE_ROLE_KEY = pick(
  process.env.SUPABASE_TEST_SERVICE_ROLE_KEY!,
  process.env.SUPABASE_PROD_SERVICE_ROLE_KEY!,
);

export const CLERK_PUBLISHABLE_KEY = pick(
  process.env.CLERK_TEST_PUBLISHABLE_KEY!,
  process.env.CLERK_PROD_PUBLISHABLE_KEY!,
);

export const CLERK_SECRET_KEY = pick(
  process.env.CLERK_TEST_SECRET_KEY!,
  process.env.CLERK_PROD_SECRET_KEY!,
);

export const STRIPE_PUBLISHABLE_KEY = pick(
  process.env.STRIPE_TEST_PUBLISHABLE_KEY!,
  process.env.STRIPE_PROD_PUBLISHABLE_KEY!,
);

export const STRIPE_SECRET_KEY = pick(
  process.env.STRIPE_TEST_SECRET_KEY!,
  process.env.STRIPE_PROD_SECRET_KEY!,
);

export const STRIPE_WEBHOOK_SECRET = pick(
  process.env.STRIPE_TEST_WEBHOOK_SECRET!,
  process.env.STRIPE_PROD_WEBHOOK_SECRET!,
);

export const RESEND_API_KEY = pick(
  process.env.RESEND_TEST_API_KEY!,
  process.env.RESEND_PROD_API_KEY!,
);

export const EMAIL_FROM = pick(
  process.env.EMAIL_FROM_TEST ?? 'noreply@test.medizzconnect.com',
  process.env.EMAIL_FROM_PROD ?? 'noreply@medizzconnect.com',
);

export const APP_URL = pick(
  process.env.APP_URL_TEST ?? 'http://localhost:3000',
  process.env.APP_URL_PROD ?? 'https://medizzconnect.com',
);

// ── Env-Validation mit Zod ──

export const env = createEnv({
  skipValidation: !!process.env.SKIP_ENV_VALIDATION,
  server: {
    APP_ENV: z.enum(['test', 'production']).default('test'),
    NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
    SUPABASE_TEST_URL: z.string().url(),
    SUPABASE_TEST_ANON_KEY: z.string().min(1),
    SUPABASE_TEST_SERVICE_ROLE_KEY: z.string().min(1),
    SUPABASE_PROD_URL: z.string().url().optional(),
    SUPABASE_PROD_ANON_KEY: z.string().min(1).optional(),
    SUPABASE_PROD_SERVICE_ROLE_KEY: z.string().min(1).optional(),
    CLERK_TEST_SECRET_KEY: z.string().min(1),
    CLERK_PROD_SECRET_KEY: z.string().min(1).optional(),
    STRIPE_TEST_SECRET_KEY: z.string().min(1),
    STRIPE_PROD_SECRET_KEY: z.string().min(1).optional(),
    STRIPE_MODE: z.enum(['test', 'live']).default('test'),
    RESEND_TEST_API_KEY: z.string().min(1).optional(),
    RESEND_PROD_API_KEY: z.string().min(1).optional(),
    CRON_SECRET: z.string().min(1).optional(),
    DB_PROVIDER: z.enum(['supabase', 'prisma']).default('supabase'),
    AUTH_PROVIDER: z.enum(['clerk', 'nextauth']).default('clerk'),
    PAYMENT_PROVIDER: z.enum(['stripe', 'manual']).default('stripe'),
    STORAGE_PROVIDER: z.enum(['supabase', 's3', 'minio']).default('supabase'),
  },
  client: {
    NEXT_PUBLIC_APP_ENV: z.enum(['test', 'production']).default('test'),
    NEXT_PUBLIC_SUPABASE_URL: z.string().url().optional(),
    NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1).optional(),
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: z.string().min(1).optional(),
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().min(1).optional(),
    NEXT_PUBLIC_APP_URL: z.string().url().optional(),
    NEXT_PUBLIC_ERKLAERVIDEO_BASE_URL: z.string().url().optional(),
  },
  runtimeEnv: {
    APP_ENV: process.env.APP_ENV,
    NODE_ENV: process.env.NODE_ENV,
    SUPABASE_TEST_URL: process.env.SUPABASE_TEST_URL,
    SUPABASE_TEST_ANON_KEY: process.env.SUPABASE_TEST_ANON_KEY,
    SUPABASE_TEST_SERVICE_ROLE_KEY: process.env.SUPABASE_TEST_SERVICE_ROLE_KEY,
    SUPABASE_PROD_URL: process.env.SUPABASE_PROD_URL,
    SUPABASE_PROD_ANON_KEY: process.env.SUPABASE_PROD_ANON_KEY,
    SUPABASE_PROD_SERVICE_ROLE_KEY: process.env.SUPABASE_PROD_SERVICE_ROLE_KEY,
    CLERK_TEST_SECRET_KEY: process.env.CLERK_TEST_SECRET_KEY,
    CLERK_PROD_SECRET_KEY: process.env.CLERK_PROD_SECRET_KEY,
    STRIPE_TEST_SECRET_KEY: process.env.STRIPE_TEST_SECRET_KEY,
    STRIPE_PROD_SECRET_KEY: process.env.STRIPE_PROD_SECRET_KEY,
    STRIPE_MODE: process.env.STRIPE_MODE,
    RESEND_TEST_API_KEY: process.env.RESEND_TEST_API_KEY,
    RESEND_PROD_API_KEY: process.env.RESEND_PROD_API_KEY,
    CRON_SECRET: process.env.CRON_SECRET,
    DB_PROVIDER: process.env.DB_PROVIDER,
    AUTH_PROVIDER: process.env.AUTH_PROVIDER,
    PAYMENT_PROVIDER: process.env.PAYMENT_PROVIDER,
    STORAGE_PROVIDER: process.env.STORAGE_PROVIDER,
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_ERKLAERVIDEO_BASE_URL: process.env.NEXT_PUBLIC_ERKLAERVIDEO_BASE_URL,
  },
});

// ── Sicherheitscheck: Stripe-Mode MUSS zu APP_ENV passen ──

export function validatePaymentEnvironment(): void {
  const stripeMode = process.env.STRIPE_MODE ?? 'test';
  if (isProduction && stripeMode !== 'live') {
    throw new Error(
      '❌ SICHERHEITSFEHLER: APP_ENV=production aber STRIPE_MODE=test! Setze STRIPE_MODE=live für Production.',
    );
  }
  if (!isProduction && stripeMode === 'live') {
    throw new Error(
      '❌ SICHERHEITSFEHLER: APP_ENV=test aber STRIPE_MODE=live! Live-Zahlungen in Test-Umgebung nicht erlaubt.',
    );
  }
}

export { APP_ENV, isProduction, pick };
